import { useMemo } from 'react';
import { Reply } from 'lucide-react';
import { HStack, VStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import type { InfoItemDescriptor } from '@components/base/display';
import { Button } from '@components/base/actions';
import type { ActionsConfig, CustomActionConfig } from '@components/integrated/entity-form';
import {
  applyItemOverrides,
  applyLayoutOverrides,
  buildPublishedConfigFromInfoItems,
  usePublishLayoutConfig,
  withComponentRules,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import type {
  VeosMailAttachment,
  VeosClassementOption,
  VeosDocumentRow,
} from '../../api/documentsService';
import { EmailAttachmentRow } from './EmailAttachmentRow';
import { isFileNameInGed } from './documentHelpers';
import { ATTACHMENT_GED_CONFIG_ID, buildAttachmentGedConfig } from './documentClassement';

/**
 * Normalized email shape rendered by `EmailView`. Both sent emails
 * (`mail-get`) and received emails (parsed `.msg`/EML) are mapped to this.
 */
export interface EmailViewModel {
  from: string;
  to: string;
  date: string;
  subject: string;
  body: string;
  /** When true, the body is HTML and rendered in a sandboxed iframe. */
  bodyIsHtml?: boolean;
  attachments: VeosMailAttachment[];
}

interface EmailViewProps {
  email: EmailViewModel;
  classementOptions: VeosClassementOption[];
  documents: VeosDocumentRow[];
  idSin: string;
  /**
   * Resolves an attachment's base64 content (lazy — `data` is null on the
   * listing). Optional to mirror the other claimflow components' page-wired
   * runtime callbacks (`DocumentPreview.onSaved`), which lets the registration
   * `propsSchema` omit it cleanly — `EmailDetail` (and `EmailAttachmentModal`,
   * for an email attached to an email) always passes it at the real render
   * site. Falls back to the attachment's inline `data` when unset.
   */
  resolveAttachment?: (attachment: VeosMailAttachment) => Promise<string>;
  /**
   * Called after an attachment is filed into the GED so the host can refresh
   * its document list. Optional for the same page-wired reason as
   * `resolveAttachment` — `EmailDetail` always supplies it; defaults to a no-op.
   */
  onGedded?: () => void;
  /**
   * Called by the "Répondre" button under the header rows (mahhp 22227). The
   * button only renders when this is set: the host decides which e-mails can
   * be replied to (`EmailDetail` passes it for received GED e-mails only — see
   * `isReplyableEmail`) and where the request goes (the claim view's Email
   * card). A page-wired runtime callback like `onGedded`, so it is not part of
   * the registration `propsSchema`.
   */
  onReply?: () => void;
  /** Optional banner shown above the email (e.g. best-effort parse notice). */
  notice?: string;
  /**
   * Tenant-scoped layout overrides, injected by the `withComponentRules` HOC
   * (do not pass by hand). With no rules authored this is `undefined` and the
   * render is identical to the pre-migration view. Three surfaces consume it:
   * the `items` bucket — falling back to `fields`, which is what the rule
   * engine actually emits — drives the header rows, keyed by the stable
   * descriptor keys built in {@link buildEmailHeaderItems} (`from` / `to` /
   * `date` / `subject`); the `fields` + `actions` buckets drive each attachment
   * row's GED controls (mahhp 22843), keyed `classement` / `libelle` / `submit`
   * (see `buildAttachmentGedConfig`); and the `actions` bucket's `reply` key
   * relabels / hides the header "Répondre" button (mahhp 22227, see
   * {@link EMAIL_HEADER_ACTIONS}).
   */
  _layoutOverrides?: LayoutOverrides;
}

// ---------------------------------------------------------------------------
// Header actions config (mahhp 22227)
//
// The "Répondre" button, right-aligned under the De / À / Date rows, is a custom
// action keyed `reply` — label / variant / size / tooltip live here, never
// inline in JSX. It shares the `actions` override bucket with the attachment
// GED "Valider" (`submit`) without collision, is published with the rest of the
// view's layout config, and is merged with `_layoutOverrides.actions.reply`
// (`setActionLabel` / `hideAction`), like `DocumentsCard`'s "Ouvrir" button.
// The view renders it only when the host wires `onReply`; `handler` only names
// the action (the click calls `onReply`).
// ---------------------------------------------------------------------------

/** Stable key of the "Répondre" action — the contract a rule targets. */
const REPLY_ACTION_KEY = 'reply';

const REPLY_ACTION: CustomActionConfig = {
  id: REPLY_ACTION_KEY,
  handler: 'claimflow-emailReply',
  label: 'Répondre',
  variant: 'secondary',
  size: 'sm',
};

const EMAIL_HEADER_ACTIONS: ActionsConfig = { custom: [REPLY_ACTION] };

/** Hover hints of the header actions (no override bucket targets them). */
const EMAIL_HEADER_TOOLTIPS = {
  [REPLY_ACTION_KEY]: 'Répondre à cet e-mail depuis la carte Email',
} as const;

// ---------------------------------------------------------------------------
// Declarative header descriptor — stable keys + config-driven labels/values
//
// The four read-only header rows ("De :", "À :", "Date :", "Objet :") render
// from this ordered descriptor with stable keys (`from` / `to` / `date` /
// `subject`) — the `EmailViewModel` property names, the permanent contract a
// tenant rule targets. The French labels are display-only and overridable. The
// `subject` row is the only conditional one (the pre-migration view only showed
// it when `email.subject` was truthy): its `visible` defaults to
// `Boolean(email.subject)`, but the descriptor stays present so a rule can
// still rename or target it. `order` (1-4) reproduces the historic top-to-
// bottom order; a `reorderFields` override may change it.
// ---------------------------------------------------------------------------

/** Stable header field keys — the contract a tenant rule targets. */
const HEADER_KEY = {
  from: 'from',
  to: 'to',
  date: 'date',
  subject: 'subject',
} as const;

function buildEmailHeaderItems(email: EmailViewModel): InfoItemDescriptor[] {
  return [
    { key: HEADER_KEY.from, label: 'De :', value: email.from, order: 1, visible: true },
    { key: HEADER_KEY.to, label: 'À :', value: email.to, order: 2, visible: true },
    { key: HEADER_KEY.date, label: 'Date :', value: email.date, order: 3, visible: true },
    {
      key: HEADER_KEY.subject,
      label: 'Objet :',
      value: email.subject,
      order: 4,
      visible: Boolean(email.subject),
    },
  ];
}

function HeaderRow({
  label,
  value,
}: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="flex gap-2">
      <Text as="span" size="sm" color="muted" className="w-14 shrink-0">
        {label}
      </Text>
      <Text as="span" size="sm" className="break-words">
        {value || '—'}
      </Text>
    </div>
  );
}

/** Uppercase muted section heading ("PIÈCES JOINTES (n)", "MESSAGE"). */
function SectionHeading({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <Text
      as="span"
      size="xs"
      weight="semibold"
      color="muted"
      className="block uppercase tracking-wide"
    >
      {children}
    </Text>
  );
}

export function EmailView({
  email,
  classementOptions,
  documents,
  idSin,
  resolveAttachment,
  onGedded,
  onReply,
  notice,
  _layoutOverrides,
}: Readonly<EmailViewProps>) {
  // Base descriptor (pure function of `email`) merged with any per-tenant
  // overrides. Baseline (no overrides) ⇒ `applyItemOverrides` returns the base
  // array unchanged, so labels/values/visibility/order are byte-identical to
  // the pre-migration inline JSX.
  //
  // `items ?? fields`, like `InfoList` (InfoList.tsx) and `OtherActionsPanel`:
  // the rule engine never emits an `items` bucket — `renameField` /
  // `hideField` / `reorderFields` / tooltip rules on the header keys land in
  // `fields` (ITEM-20261013-1215-hv3e). Sharing `fields` with the attachment
  // GED config below is safe: the key sets are disjoint (`from` / `to` /
  // `date` / `subject` vs `classement` / `libelle`), `applyItemOverrides` only
  // visits the base descriptors (unknown keys are ignored, never appended),
  // and `applyLayoutOverrides` → `applyFieldOverrides` likewise only merges
  // keys already in the GED config's `fields` map.
  const baseItems = buildEmailHeaderItems(email);
  const items = applyItemOverrides(
    baseItems,
    _layoutOverrides?.items ?? _layoutOverrides?.fields,
  );

  // Each attachment row's GED controls (category select + document-name input
  // + "Valider", mahhp 22843) render from one declarative config merged with
  // the `fields` / `actions` buckets of the same overrides. Baseline (no
  // overrides) ⇒ `applyLayoutOverrides` returns the base config unchanged.
  const baseGedConfig = useMemo(
    () => buildAttachmentGedConfig(ATTACHMENT_GED_CONFIG_ID),
    [],
  );
  const gedFormConfig = useMemo(
    () => applyLayoutOverrides(baseGedConfig, _layoutOverrides),
    [baseGedConfig, _layoutOverrides],
  );

  // Publish both base surfaces under the one registered id so the admin
  // layout editor surfaces the four header rows (items) AND the attachment GED
  // controls (fields + the submit action) — the editor emits targets per
  // present bucket, like `SettlementCard`'s mixed publish — plus the header
  // "Répondre" action (a `custom` entry next to the GED `submit`). Published
  // whether or not the host wires `onReply`, so the target list is stable.
  // The `title` is the registration display name, kept from the header-only
  // publish.
  usePublishLayoutConfig('claimflow.emailView', {
    ...buildPublishedConfigFromInfoItems(baseItems, 'Email View'),
    fields: baseGedConfig.fields,
    actions: {
      ...baseGedConfig.actions,
      custom: [...(baseGedConfig.actions?.custom ?? []), ...(EMAIL_HEADER_ACTIONS.custom ?? [])],
    },
  });

  // "Répondre": shown when the host wires `onReply` and no tenant rule hides it.
  const replyOverride = _layoutOverrides?.actions?.[REPLY_ACTION_KEY];
  const replyLabel = replyOverride?.label ?? REPLY_ACTION.label;
  const showReply =
    onReply !== undefined && (replyOverride?.visible ?? REPLY_ACTION.visible ?? true);

  // Visible rows in merged-`order` sort order — so a `hideField` override drops
  // a row and a `reorderFields` override changes their order. Each label/value
  // flows from the (override-merged) descriptor, never an inline literal.
  const visibleHeaderRows = items
    .filter((item) => item.visible !== false)
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
    );

  return (
    <VStack gap={4}>
      {notice && (
        <div className="rounded-md bg-warning-50 border border-warning-200 px-3 py-2">
          <Text size="sm" color="muted">
            {notice}
          </Text>
        </div>
      )}

      {/* Bordered inner card: header rows, attachments, body. */}
      <div className="border border-secondary-200 rounded-lg p-4">
        <VStack gap={1}>
          {visibleHeaderRows.map((item) => (
            <HeaderRow key={item.key} label={item.label ?? ''} value={item.value} />
          ))}
        </VStack>

        {/* "Répondre" (mahhp 22227) — right-aligned under the header rows,
            per the mockup; label / variant / tooltip from the actions config. */}
        {showReply && (
          <HStack justify="end" className="mt-3">
            <Button
              variant={REPLY_ACTION.variant}
              size={REPLY_ACTION.size}
              leftIcon={<Reply className="w-4 h-4" />}
              title={EMAIL_HEADER_TOOLTIPS[REPLY_ACTION_KEY]}
              onClick={onReply}
            >
              {replyLabel}
            </Button>
          </HStack>
        )}

        <div className="my-4 border-t border-secondary-200" />

        <VStack gap={2}>
          {/* Section heading: a heading label with no clean config bucket under
              the single-hostKind InfoList host — deliberate exemption (same
              precedent as EstimationCard's "0.00" mask, commit a0f3747). */}
          <SectionHeading>Pièces jointes ({email.attachments.length})</SectionHeading>
          {email.attachments.length === 0 ? (
            <Text size="sm" color="muted">
              Aucune pièce jointe.
            </Text>
          ) : (
            <VStack gap={2}>
              {email.attachments.map((att) => (
                <EmailAttachmentRow
                  key={String(att.id)}
                  attachment={att}
                  classementOptions={classementOptions}
                  alreadyInGed={isFileNameInGed(att.name, documents)}
                  // Forwarded so an email-typed attachment's nested view
                  // (opened in a modal) knows what is already in GED.
                  documents={documents}
                  idSin={idSin}
                  // `resolveAttachment` / `onGedded` are page-wired and always
                  // supplied by `EmailDetail`; the fallbacks only satisfy the
                  // optional prop types (the resolver falls back to the
                  // attachment's inline `data`).
                  resolveContent={
                    resolveAttachment ?? ((a) => Promise.resolve(a.data ?? ''))
                  }
                  onGedded={onGedded ?? (() => {})}
                  gedFormConfig={gedFormConfig}
                />
              ))}
            </VStack>
          )}
        </VStack>

        <div className="my-4 border-t border-secondary-200" />

        <VStack gap={2}>
          {/* Section heading: a heading label with no clean config bucket under
              the single-hostKind InfoList host — deliberate exemption (same
              precedent as EstimationCard's "0.00" mask, commit a0f3747). */}
          <SectionHeading>Message</SectionHeading>
          {email.bodyIsHtml ? (
            <div className="w-full aspect-[210/297] border border-secondary-200 rounded-md bg-white overflow-hidden">
              <iframe
                title="Corps du message"
                sandbox=""
                srcDoc={email.body}
                className="w-full h-full border-0"
              />
            </div>
          ) : (
            <Text size="sm" className="whitespace-pre-line">
              {email.body || '—'}
            </Text>
          )}
        </VStack>
      </div>
    </VStack>
  );
}

/**
 * Rule-aware wrapped `EmailView`. `EmailDetail` renders this constant (instead
 * of the raw `EmailView`) so admin-authored rules fire on every render — in
 * particular `renameField` / `hideField` / `reorderFields` on the four header
 * field keys (`from` / `to` / `date` / `subject`) retext, hide, or reorder the
 * read-only header rows without a code deploy, and `setFieldPlaceholder` /
 * `hideField` / `reorderFields` on `classement` / `libelle` plus
 * `setActionLabel` / `hideAction` on `submit` reshape each attachment row's
 * GED controls (mahhp 22843), and `setActionLabel` / `hideAction` on `reply`
 * relabel / hide the header "Répondre" button (mahhp 22227).
 *
 * Co-located here (not in `./register`) deliberately: `register.ts` imports
 * `DocumentsCard`, which renders `EmailDetail`, which renders this wrapped
 * view, so building the HOC wrapper in `register.ts` would create a
 * register↔DocumentsCard↔EmailDetail import cycle (a broken cycle makes the
 * imported component `undefined` and the render throws). Building it in the
 * leaf `EmailView` module avoids the cycle; the registration (id + raw
 * component + schema) still lives in `register.ts`, which imports the raw
 * `EmailView` for that purpose only. This re-export keeps every claimflow
 * `*Configurable` discoverable from the one barrel.
 *
 * The `react-refresh/only-export-components` disable below is intentional: the
 * wrapped component is co-located with the raw `EmailView` on purpose (to avoid
 * the import cycle), and both exports are components, so the only "cost" is
 * fast-refresh granularity in dev.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const EmailViewConfigurable = withComponentRules(
  EmailView,
  'claimflow.emailView',
);
